type MixedValueSortType = 'empty' | 'number' | 'date' | 'text';

interface ParsedMixedValue {
  type: MixedValueSortType;
  numberValue: number;
  dateValue: number;
  textValue: string;
}

const TYPE_ORDER: Record<MixedValueSortType, number> = {
  number: 0,
  date: 1,
  text: 2,
  empty: 3,
};

const DATE_INDICATOR_PATTERN = /\d{1,4}[/-]\d{1,2}[/-]\d{1,4}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;
const NUMBER_PATTERN = /^[($€£¥\s]*-?[\d,]+(\.\d+)?%?[)]*$/;

function parseNumberValue(value: string): number | null {
  let normalized = value.trim();
  if (!NUMBER_PATTERN.test(normalized)) {
    return null;
  }

  const isNegative = /^\(.*\)$/.test(normalized);
  if (isNegative) {
    normalized = normalized.slice(1, -1);
  }

  const isPercent = normalized.endsWith('%');
  if (isPercent) {
    normalized = normalized.slice(0, -1);
  }

  normalized = normalized
    .replace(/[$€£¥\s]/g, '')
    .replace(/,/g, '');

  if (normalized === '' || normalized === '-') {
    return null;
  }

  const num = Number(normalized);
  if (!Number.isFinite(num)) {
    return null;
  }

  const signedValue = isNegative ? -num : num;
  return isPercent ? signedValue / 100 : signedValue;
}

function parseDateValue(value: string): number | null {
  const trimmed = value.trim();
  if (!DATE_INDICATOR_PATTERN.test(trimmed) && !ISO_DATE_PATTERN.test(trimmed)) {
    return null;
  }

  const time = Date.parse(trimmed);
  if (Number.isNaN(time)) {
    return null;
  }

  const year = new Date(time).getFullYear();
  if (year < 1900 || year > 2100) {
    return null;
  }

  return time;
}

export function parseMixedValue(value: unknown): ParsedMixedValue {
  if (value == null) {
    return { type: 'empty', numberValue: 0, dateValue: 0, textValue: '' };
  }

  const text = String(value).trim();
  if (text === '') {
    return { type: 'empty', numberValue: 0, dateValue: 0, textValue: '' };
  }

  if (DATE_INDICATOR_PATTERN.test(text) || ISO_DATE_PATTERN.test(text)) {
    const dateValue = parseDateValue(text);
    if (dateValue != null) {
      return { type: 'date', numberValue: 0, dateValue, textValue: text };
    }
  }

  const numberValue = parseNumberValue(text);
  if (numberValue != null) {
    return { type: 'number', numberValue, dateValue: 0, textValue: text };
  }

  return { type: 'text', numberValue: 0, dateValue: 0, textValue: text };
}

/**
 * Compares mixed-type cell values similar to Excel's
 * "Sort anything that looks like a number, as a number" behavior.
 */
export function compareMixedValues(valueA: unknown, valueB: unknown): number {
  const parsedA = parseMixedValue(valueA);
  const parsedB = parseMixedValue(valueB);

  if (parsedA.type !== parsedB.type) {
    return TYPE_ORDER[parsedA.type] - TYPE_ORDER[parsedB.type];
  }

  switch (parsedA.type) {
    case 'number':
      return parsedA.numberValue - parsedB.numberValue;
    case 'date':
      return parsedA.dateValue - parsedB.dateValue;
    case 'text':
      return parsedA.textValue.localeCompare(parsedB.textValue, undefined, { sensitivity: 'base' });
    case 'empty':
      return 0;
  }
}

export const mixedValueComparator = (
  valueA: unknown,
  valueB: unknown,
) => compareMixedValues(valueA, valueB);
